import React from "react";
import { Link, Navigate, Outlet, useOutletContext } from "react-router-dom";
import { MdOutlineBlock } from "react-icons/md";
import useHrAccess from "../../hooks/useHrAccess";
import { hasEmployeesAccess, hasHrFeature, hasHrPermission } from "../../constants/hrPermissions";

// Wraps every HR route. HR must be switched on for the vendor (their plan), and a route
// can ask for a permission set on top (`permission="HR_ADMIN"`). The menu already hides what a
// person cannot use; this stops the same screens being reached by typing the URL. The API
// checks all of it again on every request.
function Blocked({ title, text }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "#6c757d", textAlign: "center", padding: 32 }}>
      <MdOutlineBlock size={48} style={{ marginBottom: 16, color: "#C9194A" }} />
      <h3 style={{ color: "#1a1a1a", marginBottom: 8 }}>{title}</h3>
      <p style={{ maxWidth: 380, fontSize: 14 }}>{text}</p>
      <Link to="/dashboard" className="btn btn-danger" style={{ borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
        Back to Dashboard
      </Link>
    </div>
  );
}

// `permission` is one permission set or a list (any of them will do); `feature` is an add-on switched on by the Super Admin.
// `employees` opens the route to the Employees capability every plan has (the HR plan is not needed).
// `landing` marks the HR home route: on a plan without the HR module it sends the person to the Employees screen
// instead of a dead end.
export function HrGate({ permission, feature, employees, landing }) {
  const { access, loaded } = useHrAccess();
  if (!loaded) return <div style={{ padding: 32, color: "#6c757d", fontSize: 14 }}>Loading…</div>;
  if (employees) {
    if (!hasEmployeesAccess(access)) return <Blocked title="You do not have access" text="Only the owner or an administrator of the account can manage employees." />;
    return <Outlet context={{ access }} />;
  }
  if (landing && !access.enabled && access.employees_enabled) return <Navigate to="/hr/team" replace />;
  if (!access.enabled) return <Blocked title="HR is not enabled" text="Human Resource is part of the HR plan. Contact UtilStop to enable it for your account." />;
  if (feature && !hasHrFeature(access, feature)) return <Blocked title="Not available" text="This part of HR is an add-on that has not been switched on for your account. Contact UtilStop to enable it." />;
  const wanted = permission ? [].concat(permission) : [];
  if (wanted.length && !hasHrPermission(access, ...wanted)) return <Blocked title="You do not have access" text="Ask your HR administrator to give you access to this screen." />;
  return <Outlet context={{ access }} />;
}

// What the HR screens read from the gate.
export const useHr = () => useOutletContext();

export default HrGate;
